
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from 'sonner';
import { Copy, RefreshCw } from 'lucide-react';

export const MetaTagViewer = () => {
  const [metaTags, setMetaTags] = useState<{ name: string; content: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchMetaTags = () => {
    setLoading(true);
    try {
      const tags: { name: string; content: string }[] = [];
      
      // Get all meta tags
      document.querySelectorAll('meta').forEach((meta) => {
        // Get the identifier (name, property, http-equiv)
        let name = meta.getAttribute('name') || 
                   meta.getAttribute('property') || 
                   meta.getAttribute('http-equiv') || 
                   'unknown';
        
        // Get content
        const content = meta.getAttribute('content') || '';
        
        tags.push({ name, content });
      });
      
      // Also get title
      const titleTag = document.querySelector('title');
      if (titleTag) {
        tags.push({ name: 'title', content: titleTag.textContent || '' });
      }
      
      // Sort alphabetically by name
      tags.sort((a, b) => a.name.localeCompare(b.name));
      
      setMetaTags(tags);
      toast.success('Meta-Tags erfolgreich geladen');
    } catch (error) {
      console.error('Error fetching meta tags:', error);
      toast.error('Fehler beim Laden der Meta-Tags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetaTags();
  }, []);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => toast.success('In die Zwischenablage kopiert'))
      .catch(() => toast.error('Kopieren fehlgeschlagen'));
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Meta-Tags Übersicht</h2>
        <Button 
          onClick={fetchMetaTags}
          variant="outline"
          size="sm"
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      <ScrollArea className="h-[400px] border rounded-md p-4">
        <div className="space-y-4">
          {metaTags.map((tag, index) => (
            <div key={index} className="p-3 bg-slate-50 rounded-md">
              <div className="flex justify-between">
                <div className="font-medium text-blue-600">{tag.name}</div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleCopy(`${tag.name}: ${tag.content}`)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="mt-1 text-sm break-words">{tag.content}</div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
